import type { Locale } from 'date-fns';
import { endOfWeek, format, getISOWeek, isDate, parseISO, startOfWeek } from 'date-fns';

export const DATE_FORMATS = {
	display: 'MMM dd, yyyy',
	displayWithTime: 'dd MMM yyyy HH:mm',
	iso: 'yyyy-MM-dd',
	long: 'MMMM dd, yyyy',
	monthYear: 'MMMM yyyy',
	monthDayYear: 'dd MMM yyyy',
	longDayName: 'EEEE',
	shortDayName: 'EEE',
	monthNameShort: 'MMM',
	fullDateWithDayNameAndYear: 'EEEE, d MMM yyyy',
} as const;

type DateInput = Date | string | null | undefined;

export function formatDateRange(start: Date, end: Date, locale?: Locale): string {
	const opts = locale ? { locale } : undefined;
	const startStr = format(start, 'MMM d', opts);
	const endStr = format(end, 'MMM d, yyyy', opts);

	return `${startStr} - ${endStr}`;
}

export function formatDateInput(date: Date): string {
	return format(date, 'yyyy-MM-dd');
}

export function getISOWeekNumber(date: Date): number {
	return getISOWeek(date);
}

export function getWeekStart(date: Date): Date {
	return startOfWeek(date, { weekStartsOn: 1 }); // Monday
}

export function getWeekEnd(date: Date): Date {
	return endOfWeek(date, { weekStartsOn: 1 }); // Sunday
}

export function formatMonthYear(date: Date, locale?: Locale): string {
	return format(date, 'MMMM yyyy', locale ? { locale } : undefined);
}

export function formatDate(date: DateInput, formatKey: keyof typeof DATE_FORMATS = 'display', locale?: Locale): string {
	if (!date) return '';

	return format(date, DATE_FORMATS[formatKey], locale ? { locale } : undefined);
}

export function formatDateDisplay(date: DateInput, locale?: Locale): string {
	return formatDate(date, 'display', locale);
}

export function formatDateLong(date: DateInput, locale?: Locale): string {
	return formatDate(date, 'long', locale);
}

export function formatDateWithTime(date: DateInput, locale?: Locale): string {
	return formatDate(date, 'displayWithTime', locale);
}

export function formatDateMonthDayYear(date: DateInput, locale?: Locale): string {
	return formatDate(date, 'monthDayYear', locale);
}

/**
 * Converts Date objects to API-compatible date strings (yyyy-MM-dd format).
 * API requires dates without time information.
 *
 * @example
 * formatDateRangeForApi({
 *   startDate: new Date('2024-01-15T14:30:00'),
 *   endDate: new Date('2024-01-20T09:45:00')
 * })
 * // Returns: { startDate: '2024-01-15', endDate: '2024-01-20' }
 */
export function formatDateForApi(date: Date | string): string {
	return formatDate(date, 'iso');
}

/**
 * Parses an API date string. Date-only values ('yyyy-MM-dd') become local midnight,
 * unlike `new Date('yyyy-MM-dd')` which is UTC midnight and shifts the day west of UTC.
 */
export function parseDateFromApi(date: string | Date): Date {
	if (isDate(date)) return date;

	return parseISO(date);
}

/** Inclusive ISO date string range check. All three params must be 'yyyy-MM-dd' strings. */
export function isDateStringInRange(date: string, startDate: string, endDate: string): boolean {
	return date >= startDate && date <= endDate;
}
